import CmpIABService from './cmp-iab-service';
import CrossDomainConsentSharingService from './cdcs.service';
import DataExchangeService from './data-exchange.service';
import * as _layouts from '../../layouts.json';
import { ApiService } from './api-service';
import { ConsentsStatusService } from './consents-services/consents-status.service';
import { ConfigService } from './utilities/config.service';
import { DataService } from './utilities/data.service';
import { MainViewController } from '../ui-components/usercentrics-button/main-view-controller';
import { StyleService } from './style-service';
import { UrlVisibilityDeciderService } from './utilities/url-visibility-decider.service';
import { UtilityService } from './utility.service';
import { WindowUsercentricsService } from './utilities/window-usercentrics.service';
import { LanguageCode } from '../Enums/LanguageCode.enums';
const _defaultLayoutNumber = 1;
const _ = require('lodash');

export class SettingsService {
    private dataExchangeService: DataExchangeService;
    private isoverRideButton = false;
    private _apiService: ApiService;
    private _cmpIABService: CmpIABService;
    private _crossDomainConsentSharingService: CrossDomainConsentSharingService;
    private _language: any;
    private _mainViewController: MainViewController;
    private _moreInfoEvent: any;
    private _settings: any = {};
    private _utilityService: UtilityService;

    constructor(utilityService) {
        this.cmpIABService = utilityService.cmpIABService;
        this.crossDomainConsentSharingService = utilityService.crossDomainConsentSharingService;
        this.apiService = utilityService.apiService;
        this.utilityService = utilityService;
        this.dataExchangeService = new DataExchangeService();

        const firstUserInteraction = DataService.getLocalStorage('firstUserInteraction');
        if (!firstUserInteraction) {
            DataService.storeToLocalStorage('firstUserInteraction', this.firstUserInteraction);
        }
    }

    get mainViewController(): MainViewController {
        return this._mainViewController;
    }

    set mainViewController(mainViewController) {
        this._mainViewController = mainViewController;
    }

    get apiService(): ApiService {
        return this._apiService;
    }

    set apiService(apiService) {
        this._apiService = apiService;
    }

    get crossDomainConsentSharingService(): CrossDomainConsentSharingService {
        return this._crossDomainConsentSharingService;
    }

    set crossDomainConsentSharingService(crossDomainConsentSharingService) {
        this._crossDomainConsentSharingService = crossDomainConsentSharingService;
    }

    get utilityService(): UtilityService {
        return this._utilityService;
    }

    set utilityService(utilityService) {
        this._utilityService = utilityService;
    }

    get cmpIABService() {
        return this._cmpIABService;
    }

    set cmpIABService(cmpIABService) {
        this._cmpIABService = cmpIABService;
    }

    private _elementsVisibility: any = {
        primaryButton: true,
        chipButton: true,
        bannerVisible: true,
        cornerModel: false,
        centerModel: false,
        infoModel: false,
        languageService: true,
        poweredBy: true,
        moreInfoButton: true,
        closeIconCenterModal: true,
        showDeny: true,
        showMore: true,
        isEmbeddedVisible: true
    };

    private _firstUserInteraction = {
        value: 1,
        stateSaved: false,
    };

    private firstUserInteractionMapper = [
        {
            value: 0, // same as 1,
            stateSaved: false,
            target: 'banner',
            description: 'Open Banner (default)',
        },
        {
            value: 1,
            stateSaved: false,
            target: 'banner',
            description: 'Open Banner (default)',
        },
        {
            value: 2,
            stateSaved: false,
            target: 'centerModal',
            isClosable: false,
            description: 'Open Window in Center (not closable)',
        },
        {
            value: 3,
            stateSaved: false,
            target: 'centerModal',
            isClosable: true,
            description: 'Open Window in Center (closable)',
        },
        {
            value: 4,
            stateSaved: false,
            target: null,
            description: 'Display nothing',
        },
        {
            value: 5,
            stateSaved: false,
            target: 'centerModal',
            isClosable: false,
            isPreselect: true,
            description: 'Open Window in Center (not closable and Pre-select all Consents but not Activate Them)',
        },
        {
            value: 6,
            stateSaved: false,
            target: 'centerModal',
            isClosable: true,
            isPreselect: true,
            description: 'Open Window in Center (closable and Pre-select all Consents but not Activate Them)',
        },
        {
            value: 7,
            stateSaved: false,
            target: 'banner',
            description: 'Open Central Banner',
        },
    ];

    static get layouts() {
        return _layouts['layouts'];
    }

    get elementsVisibility() {
        return this._elementsVisibility;
    }

    set settings(_settings: any) {
        this._settings = _settings;
        this._language = _settings.language || LanguageCode.default;
    }

    get settings(): any {
        const cachedSettings = this._settings;
        if (cachedSettings.settingsId && cachedSettings) {
            return cachedSettings;
        }
        const storedSettings = DataService.getLocalStorage('settings');
        return storedSettings || { language: LanguageCode.default };
    }

    set privacyButtonIsVisible(_privacyButtonIsVisible) {
        if (!this.isoverRideButton) {
            this._elementsVisibility.primaryButton = _privacyButtonIsVisible;
        }
    }

    updateButtonVisibility(_privacyButtonIsVisible, isoverRideButton?) {
        this.isoverRideButton = true;
        this._elementsVisibility.primaryButton = _privacyButtonIsVisible;
    }

    set chipIsVisible(_chipIsVisible) {
        this._elementsVisibility.chipButton = _chipIsVisible;
    }

    set bannerIsVisible(_banner) {
        this._elementsVisibility.bannerVisible = _banner;
    }

    set cornerModelIsVisible(_cornerModel) {
        this._elementsVisibility.cornerModel = _cornerModel;
    }

    set centerModelIsVisible(_centerModel) {
        this._elementsVisibility.centerModel = _centerModel;
    }

    set infoModelIsVisible(_infoModel) {
        this._elementsVisibility.infoModel = _infoModel;
    }

    set firstUserInteraction(_firstUserInteraction) {
        if (!_firstUserInteraction) {
            return;
        }
        this._firstUserInteraction = _firstUserInteraction;

        if (_firstUserInteraction.value && _firstUserInteraction.value === 2) {
            this._elementsVisibility.closeIconCenterModal = _firstUserInteraction.stateSaved;
        }

        DataService.storeToLocalStorage('firstUserInteraction', _firstUserInteraction);
    }

    get firstUserInteraction() {
        return DataService.getLocalStorage('firstUserInteraction') || this._firstUserInteraction;
    }

    set language(_language) {
        this._language = _language || LanguageCode.default;
        const settings: any = _.cloneDeep(this.settings);
        settings.language = this._language;
        this.settings = settings;
        DataService.storeToGlobalVariable('settings', this.settings);
    }

    get language() {
        const settings = DataService.getLocalStorage('settings');
        const storedLanguage = settings && settings.language;
        return this._language || storedLanguage;
    }

    private initializeFromSetting(setting) {
        this.utilityService.developmentLog('Initialiting Application from local storage');
        this.saveToStorage(setting);
        if (setting.iabConsentIsActive) {
            this.cmpIABService.turnOnIABService();
        }

        if (setting.consentSharingIFrameIsActive) {
            if (!setting.consentSharingIFrameUrl) {
                this.crossDomainConsentSharingService.turnOnCrossDomainConsentSharing(this.firstUserInteraction.stateSaved);
            } else {
                this.crossDomainConsentSharingService.turnOnCrossDomainConsentSharing(
                    this.firstUserInteraction.stateSaved,
                    setting.consentSharingIFrameUrl
                );
                this.initializeCrossDomainEventListener();
            }
        }
    }

    /**
     *
     * Here we first initialize the dataExchangeService with local Storage Categories or empty array is passed if the categories in the local storage is empty. This may happen when it is the first time page load.
     * After that we are initializing the settings. We get the settings from the server. when we get settings from the server we also perform initialization of the entire application inside that. We do these things -
     * 1) Initialize the consents and categories - this thing happens inside another service called consents-service. Initialize consents and categories service is called.
     * //TODO - Add other things about what happens in this function.
     * After the settings or the app has been initialized the dataExchangeService is triggered with settings from the server event.
     * After that the promise is fulfilled.
     */
    async initSettings() {
        this.utilityService.developmentLog('initializing setting');

        const localStorage = DataService.getLocalStorage();

        if (localStorage && localStorage.settings) {
            this.language = localStorage.settings.language;
            this.initializeFromSetting(localStorage.settings);
        } else {
            this.language = this.utilityService.languageDeciderService.getInterfaceLanguage().language;
        }

        try {
            await this.getSettingsFromServer(this.language);
            this.utilityService.cmpIABService.getPurposeJson(this.language);
        } catch (e) {
            console.error('getSettingsFromServer threw error', e);
            throw e;
        }

        this.utilityService.developmentLog('initSettings got setting');
        this.dataExchangeService.dispatchConsentsInitializedEvent(
            DataService.consents, DataService.consentTemplates
        );
    }

    initializeCrossDomainEventListener(newSettings?, oldSettings?) {
        const self = this;
        window.addEventListener('crossDomainConsentFirstTimeNotification', function (payload: any) {
            if (payload.crossDomainConsentsExist) {
                self.utilityService.developmentLog('Cross domain consents prexist');
                if (!payload.firstTimeStateSaved) {
                    self.setFirstUserInteractionStateSavedToFalse();
                } else {
                    self.setFirstUserInteractionStateSavedToTrue();
                }
            }
            if (!payload.firstTimeStateSaved) {
                self.setFirstUserInteractionStateSavedToFalse();
            }
            if (self.mainViewController) {
                MainViewController.update();
            }
        });
    }

    denyAllCookiesExceptEssential() {
        const action = 'onDenyAllBtnClick';
        const updatedBy = 'explicit';
        this.utilityService.saveConsentsService.denyAllConsentsExceptEssential(action, updatedBy);
    }

    changeInterfaceLanguage(newLanguage) {
        let needToUpdateSettings: boolean = !!(newLanguage !== this.language);
        const settings = this.settings;
        if (needToUpdateSettings && settings && settings.languagesAvailable && settings.languagesAvailable.length && settings.languagesAvailable.indexOf(newLanguage) === -1) {
            needToUpdateSettings = false;
        }
        
        if (needToUpdateSettings) {
            this.getSettingsFromServer(newLanguage).then(
                event => {
                    this.utilityService.historyLabelService.momentUpdateLocale();
                    this.utilityService.cmpIABService.getPurposeJson(newLanguage);
                    setTimeout(() => {
                        this.utilityService.developmentLog(`Interface language is changed to ${newLanguage}`);
                        this.utilityService.UserCentricViewController.updateView();
                    }, 100);
                    return;
                }).catch(error => {
                    console.log(error);
                }
                );
        } else {
            this.utilityService.UserCentricViewController.updateView();
        }
    }

    // deprecated needed it for older settings
    static updateSettingMapping(settings) {
        const response = JSON.parse(JSON.stringify(settings));
        const layoutNumber = _.cloneDeep(response.layout || response.templateName || _defaultLayoutNumber);

        response.layout = 'layout-' + layoutNumber;
        // If consentStatus is missing, set it to null
        if (Array.isArray(response.consentTemplates)) {
            response.consentTemplates.forEach(consent => {
                if (!consent.hasOwnProperty('defaultConsentStatus')) {
                    consent.defaultConsentStatus = false;
                }
            });
        }

        response.styles.cornerModalHeaderBgColor = response.styles.cornerModalHeaderBgColor
            ? response.styles.cornerModalHeaderBgColor
            : response.styles.btnPrivacyButtonBgColor;

        response.styles.bannerBgColor = response.styles.bannerBgColor
            ? response.styles.bannerBgColor
            : response.styles.btnPrivacyButtonBgColor;

        response.styles.cornerModalHeaderTextColor = response.styles.cornerModalHeaderTextColor
            ? response.styles.cornerModalHeaderTextColor
            : 'ffffff';

        response.styles.txtOptInMsgFontSize = response.styles.txtOptInMsgFontSize
            ? response.styles.txtOptInMsgFontSize
            : 14;

        /**
         * if response.bannerPlacement == 1 => set banner to top
         * if response.bannerPlacement == 2 => set banner to Bottom
         */
        response.bannerPlacement = response.bannerPlacement
            ? response.bannerPlacement
            : !response.buttonDisplayLocation ||
                response.buttonDisplayLocation === 'br' ||
                response.buttonDisplayLocation === 'bl'
                ? 1
                : 2;

        /**
         * if response.cornerModalPlacement == 1 => set modal to left
         * if response.cornerModalPlacement == 2 => set modal to right
         */
        response.cornerModalPlacement = response.cornerModalPlacement
            ? response.cornerModalPlacement
            : !response.buttonDisplayLocation ||
                response.buttonDisplayLocation === 'tl' ||
                response.buttonDisplayLocation === 'bl'
                ? 1
                : 2;

        /**
         * if response.languageSelectorType == 1 => do not display language dropdown
         * if response.languageSelectorType == 2 => set language dropdown to dispolay flags
         * if response.languageSelectorType == 3 => set language dropdown to dispolay language code
         */
        response.languageSelectorType = response.languageSelectorType
            ? response.languageSelectorType
            : response.showLanguageDropdown
                ? 2
                : 1;

        /**
         * if response.privacyModal == 1 => set to display corner modal
         * if response.privacyModal == 2 => set to display center modal
         */
        response.privacyModal = response.privacyModal
            ? response.privacyModal
            : !response.privacyButtonIsVisible && response.chipIsVisible
                ? 2
                : 1;

        response.styles.modalSaveTextColor = response.styles.modalSaveTextColor
            ? response.styles.modalSaveTextColor
            : response.styles.cornerModalHeaderTextColor;
        response.styles.modalSaveBgColor = response.styles.modalSaveBgColor
            ? response.styles.modalSaveBgColor
            : response.styles.cornerModalHeaderBgColor;
        return response;
    }

    async getSettingsFromServer(language) {
        this.utilityService.developmentLog('getting setting from server in ', language);

        try {
            let response = await this.apiService.getSettingsFromServer(language);

            if (response instanceof Object) {
                response = JSON.parse(JSON.stringify(response));

                if (this.utilityService.languageDeciderService.fallbackUsed && response.languagesAvailable) {
                    this.utilityService.languageDeciderService.resetFallbackUsed();
                    const newLanguage = this.utilityService.languageDeciderService.getFirstAvailableLanguage(
                        response.languagesAvailable
                    );

                    if (newLanguage !== language) {
                        await this.getSettingsFromServer(newLanguage);
                        return;
                    }

                    this.utilityService.developmentLog(
                        'fallback language is same as current language so no need to call again'
                    );
                }

                if (response.languagesAvailable && response.languagesAvailable.length && response.languagesAvailable.indexOf(language) === -1) {
                    await this.getSettingsFromServer(response.languagesAvailable[0]);
                    return;
                }

                const oldSettings = _.cloneDeep(this.settings);

                if (ConfigService.areInteractionEventsActivated()) {

                    let dataExchangeOnPage = response.dataExchangeOnPage;
                    for (const i in dataExchangeOnPage) {

                        if (Object.prototype.hasOwnProperty.call(dataExchangeOnPage, i)) {
                            dataExchangeOnPage[i].events.push('uc_privacySettingsCenterClosed');
                            dataExchangeOnPage[i].events.push('uc_privacySettingsSideClosed');
                            dataExchangeOnPage[i].events.push('uc_privacyInformationCenterClosed');
                        }
                    }
                    response.dataExchangeOnPage = dataExchangeOnPage;
                }

                this.dataExchangeService.dataExchangeArray = response.dataExchangeOnPage;
                response = SettingsService.updateSettingMapping(response);
                WindowUsercentricsService.updateFromSetting(response);

                this.utilityService.developmentLog('got setting from server ', response);
                const infoModalTranslations = (language === 'de') ? require('../../infoModalLabels.de.json') : require('../../infoModalLabels.en.json');

                for (const label in infoModalTranslations) {
                    response.labels[label] = infoModalTranslations[label];
                }

                this.saveToStorage(response);
                const firstUserInteractionValue = _.isBoolean(response.firstUserInteraction)
                    ? 0
                    : response.firstUserInteraction;

                if (response.consentSharingIFrameIsActive) {
                    if (response.consentSharingIFrameUrl) {
                        this.crossDomainConsentSharingService.turnOnCrossDomainConsentSharing(
                            this.firstUserInteraction.stateSaved,
                            response.consentSharingIFrameUrl
                        );
                    } else {
                        this.crossDomainConsentSharingService.turnOnCrossDomainConsentSharing(
                            this.firstUserInteraction.stateSaved
                        );
                    }

                    this.initializeCrossDomainEventListener(response, oldSettings);
                    this._firstUserInteraction.value = response.firstUserInteraction;

                    if (response.firstUserInteraction === 7) {
                        this.bannerIsVisible = true;
                    }
                } else {
                    this.crossDomainConsentSharingService.turnOffCrossDomainConsentSharing();
                    this.initFirstUserInteraction(firstUserInteractionValue);
                }

                this.settings = response;
                this.saveToStorage(response);
                DataService.storeGlobally('settings', response);
                let interfaceVisibility = true;

                if (this.settings.privacyButtonUrls !== null && typeof this.settings.privacyButtonUrls === 'object') {
                    interfaceVisibility = UrlVisibilityDeciderService.checkButtonVisibility(this.settings.privacyButtonUrls);
                }

                this.mapElementsVisibilityProperties(this.settings);

                if (!interfaceVisibility) {
                    this.changeButtonVisibility(false);
                }

                this.utilityService.developmentLog(
                    'setting service is trying to initCategoriesAndConsents from consent service'
                );
                const isNeedToReset =
                    this.checkNewDataWithStoredForReset(response, oldSettings) &&
                    response.resetConsentsOnShowingInitialView;

                try {
                    await this.utilityService.initializeConsentsService.initCategoriesAndConsents(
                        this.settings,
                        isNeedToReset
                    );
                } catch (error) {
                    console.error(error);
                    this.hideUsercentricsView();
                }

                if (response.iabConsentIsActive) {
                    this.cmpIABService.turnOnIABService();
                } else {
                    this.cmpIABService.turnOffIabService();
                }

                this.utilityService.developmentLog('setting service successfully inited consents');

                if (!this.firstUserInteraction.stateSaved && this.firstUserInteraction.isPreselect) {
                    this.utilityService.developmentLog('calling updateSelectedStatusForAllCategories');
                    ConsentsStatusService.updateAllConsentsStatus(DataService.consents, true);
                    this.utilityService.developmentLog('called updateSelectedStatusForAllCategories');
                }

                if (this.settings.stylesCss && this.settings.stylesCss !== '') {
                    this.utilityService.developmentLog('calling updateSelectedStatusForAllCategories');
                    StyleService.injectClientCss(this.settings.stylesCss, this.settings.layout);
                }

                DataService.updateSettingsStorage(response.settingsId);
                this.utilityService.developmentLog('setting moreInfoEvent');
                this.moreInfoEvent = this.settings.btnMoreInfoActionSelection;
                this.utilityService.developmentLog('fulfilled getSettingFromServer');
            } else {
                const nextLang = this.utilityService.languageDeciderService.getFallbackLanguage(language);

                if (nextLang) {
                    await this.getSettingsFromServer(nextLang);
                }
            }

            return;
        } catch (error) {
            console.log(error);
            const nextLang = this.utilityService.languageDeciderService.getFallbackLanguage(language);

            if (nextLang) {
                await this.getSettingsFromServer(nextLang);
            }
        }
    }

    hideUsercentricsView() {
        Object.keys(this._elementsVisibility).forEach(key => {
            this._elementsVisibility[key] = false;
        });
    }

    changeButtonVisibility(isVisible) {
        this._elementsVisibility.primaryButton = isVisible;
        this._elementsVisibility.chipButton = isVisible;
    }

    mapElementsVisibilityProperties(settings) {
        this._elementsVisibility = {
            primaryButton: this.isoverRideButton
                ? this._elementsVisibility.primaryButton
                : settings.privacyButtonIsVisible,
            chipButton: settings.chipIsVisible,
            bannerVisible: this.bannerVisibility,
            cornerModel: this._elementsVisibility.cornerModel,
            centerModel: this._elementsVisibility.centerModel || this.centerModalVisibility,
            infoModel: this._elementsVisibility.infoModel,
            languageService: settings.languageSelectorType
                ? settings.languageSelectorType
                : settings.showLanguageDropdown
                    ? 2
                    : 1,
            poweredBy: settings.enablePoweredBy,
            moreInfoButton: settings.btnMoreInfoIsVisible,
            showDeny: settings.btnDenyIsVisible,
            showMore: settings.btnMoreInfoIsVisible,
            closeIconCenterModal: this.centerModalCloseBtnVisibility,
            isEmbeddedVisible: true
        };
    }

    initFirstUserInteraction(interactionValue) {
        interactionValue = _.isBoolean(interactionValue) ? 0 : interactionValue;
        if (!this.firstUserInteraction.stateSaved) {
            this.firstUserInteraction = _.find(this.firstUserInteractionMapper, {
                value: interactionValue
            });
        }
    }

    saveFirstUserInteraction() {
        this.crossDomainConsentSharingService.firstTimeStateSaved(true);
        this.setFirstUserInteractionStateSavedToTrue();
    }

    resetFirstUserInteraction() {
        this.crossDomainConsentSharingService.firstTimeStateSaved(false);
        this.setFirstUserInteractionStateSavedToFalse();
    }

    setFirstUserInteractionStateSavedToTrue() {
        const userInteraction = _.cloneDeep(this.firstUserInteraction);
        this.firstUserInteraction = _.assign(userInteraction, { stateSaved: true });
        this.utilityService.optinTeimeoutService.stopTimer();
        switch (this.firstUserInteraction.target) {
            case 'banner':
                this.bannerIsVisible = false;
                break;
            case 'centerModal':
                this.centerModelIsVisible = false;
                break;
            default:
                break;
        }
    }

    setFirstUserInteractionStateSavedToFalse() {
        const interactionValue = this.firstUserInteraction.value;
        this.firstUserInteraction = _.assign(_.find(this.firstUserInteractionMapper, { value: interactionValue }), {
            stateSaved: false
        });
        this.mapElementsVisibilityProperties(this.settings);
    }

    checkNewDataWithStoredForReset(newSettings, storedSettings) {
        const self = this;
        let needToReset = false;
        const propertiesToCheck = ['settingsId', 'version'];
        if (!storedSettings.settingsId) {
            return false;
        }
        _.forEach(propertiesToCheck, (property: any) => {
            if (!needToReset) {
                if (property === 'version') {
                    needToReset =
                        (!_.isEqual(newSettings.showInitialViewForVersionChange.indexOf('major'), -1) &&
                            !_.isEqual(newSettings[property].split('.')[0], storedSettings[property].split('.')[0])) ||
                        (!_.isEqual(newSettings.showInitialViewForVersionChange.indexOf('minor'), -1) &&
                            !_.isEqual(newSettings[property].split('.')[1], storedSettings[property].split('.')[1])) ||
                        (!_.isEqual(newSettings.showInitialViewForVersionChange.indexOf('patch'), -1) &&
                            !_.isEqual(newSettings[property].split('.')[2], storedSettings[property].split('.')[2]));
                    self.utilityService.developmentLog(newSettings[property].split('.')[0]);
                    self.utilityService.developmentLog(storedSettings[property].split('.')[0]);
                } else if (property === 'settingsId') {
                    const isClearStorageAndReload = !_.isEqual(newSettings[property], storedSettings[property]);
                    if (isClearStorageAndReload) {
                        localStorage.removeItem('usercentrics');
                        window.location.reload();
                    }
                }
            }
        });
        if (needToReset) {
            this.resetFirstUserInteraction();
        }

        return needToReset;
    }

    saveToStorage(settings) {
        DataService.storeToGlobalVariable('settings', settings);
        window.usercentrics = window.usercentrics || {};
        window.usercentrics.settings = this.settings;
        DataService.storeToLocalStorage('settings', settings);
    }

    acceptCookiesAgreement(action = 'onAcceptAllBtnClick', updatedBy = 'explicit') {
        this.utilityService.saveConsentsService.acceptAllConsents(action, updatedBy);
    }

    get bannerVisibility() {
        this.utilityService.developmentLog('firstuserinteraction', this.firstUserInteraction);
        return this.firstUserInteraction.target === 'banner' && !this.firstUserInteraction.stateSaved;
    }

    get centerModalVisibility() {
        return this.firstUserInteraction.target === 'centerModal' && !this.firstUserInteraction.stateSaved;
    }

    get centerModalCloseBtnVisibility() {
        return (
            this.firstUserInteraction.target === 'centerModal' &&
            !this.firstUserInteraction.stateSaved &&
            this.firstUserInteraction.isClosable
        );
    }

    get cornerModalVisibility() {
        return false;
    }

    get infoModalVisibility() {
        return false;
    }

    public get moreInfoEvent() {
        return this._moreInfoEvent;
    }
    public set moreInfoEvent(event) {
        this._moreInfoEvent = event;
    }

    reset(): void {
        this.settings = undefined;
        DataService.storeToGlobalVariable('settings', undefined);
        DataService.removeLocalStorage();
    }
}
